import { Link } from "react-router-dom";
import { ArrowUpRight, Scale, Sparkles, ShieldCheck, HeartHandshake } from "lucide-react";

function Eyebrow({ children }) {
  return (
    <div className="flex items-center gap-2 font-mono-plex text-[11px] font-semibold uppercase tracking-[0.22em] text-zinc-500">
      <span className="h-[6px] w-[6px] bg-orange-500" />
      {children}
    </div>
  );
}

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="border-b border-zinc-200">
        <div className="mx-auto max-w-[1400px] px-6 pt-20 pb-16 md:px-12 md:pt-28 md:pb-24 lg:px-16">
          <Eyebrow>About Us</Eyebrow>
          <h1 className="mt-6 font-heading text-5xl font-black leading-[0.95] tracking-tighter text-zinc-950 md:text-7xl lg:text-8xl">
            Driven by quality.
            <br />
            <span className="italic font-light">Anchored</span> in responsibility.
          </h1>
          <p className="mt-10 max-w-2xl text-lg leading-relaxed text-zinc-600">
            About Agrim Ventures — a growing force in India's polymer trade with
            a commitment to social impact through skilling.
          </p>
        </div>
      </section>

      {/* STORY */}
      <section data-testid="about-story" className="border-b border-zinc-200">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-14 px-6 py-20 md:grid-cols-12 md:px-12 md:py-32 lg:px-16">
          <div className="md:col-span-4">
            <Eyebrow>01 · Our Story</Eyebrow>
            <h2 className="mt-6 font-heading text-3xl font-bold leading-tight tracking-tight text-zinc-950 md:text-5xl">
              From trading roots to trusted partnerships.
            </h2>
          </div>
          <div className="md:col-span-8">
            <p className="text-lg leading-relaxed text-zinc-700">
              Agrim Ventures Private Limited was founded with a clear vision — to
              become a trusted partner in India's polymer trade while giving
              back to the community that supports our growth.
            </p>
            <p className="mt-6 text-lg leading-relaxed text-zinc-700">
              From our beginnings in PET resin trading to becoming an authorized
              distributor for{" "}
              <span className="text-zinc-950">Chiripal Polyfilms Ltd</span>, our
              journey has been defined by integrity, reliability, and a
              commitment to excellence. Today, we serve customers through a
              wide distribution network and continue to expand our footprint —
              both commercially and socially.
            </p>
          </div>
        </div>
      </section>

      {/* VISION / MISSION */}
      <section
        data-testid="about-vision-mission"
        className="border-b border-zinc-200 bg-zinc-50"
      >
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-6 py-20 md:grid-cols-2 md:px-12 md:py-32 lg:px-16">
          <div className="border border-zinc-200 bg-white p-10">
            <div className="font-mono-plex text-[11px] uppercase tracking-[0.22em] text-orange-500">
              Vision
            </div>
            <p className="mt-6 font-heading text-2xl font-bold leading-snug tracking-tight text-zinc-950 md:text-3xl">
              To be a leading name in India's polymer trade and distribution
              industry while creating lasting social value through skilling and
              community empowerment.
            </p>
          </div>
          <div className="border border-zinc-200 bg-zinc-950 p-10 text-white">
            <div className="font-mono-plex text-[11px] uppercase tracking-[0.22em] text-orange-400">
              Mission
            </div>
            <p className="mt-6 font-heading text-2xl font-bold leading-snug tracking-tight md:text-3xl">
              To deliver premium-quality PET resin and polymer products with
              unmatched reliability, build long-term partnerships, and invest in
              building India's future workforce through impactful CSR
              initiatives.
            </p>
          </div>
        </div>
      </section>

      {/* CORE VALUES */}
      <section data-testid="about-values" className="border-b border-zinc-200">
        <div className="mx-auto max-w-[1400px] px-6 py-20 md:px-12 md:py-32 lg:px-16">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
            <div>
              <Eyebrow>02 · Core Values</Eyebrow>
              <h2 className="mt-6 font-heading text-3xl font-bold leading-tight tracking-tight text-zinc-950 md:text-5xl">
                Four principles that guide every decision.
              </h2>
            </div>
          </div>

          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: Scale,
                title: "Integrity",
                desc: "We operate with transparency and honesty in every transaction.",
              },
              {
                icon: Sparkles,
                title: "Quality",
                desc: "We source and deliver only materials that meet the highest industry standards.",
              },
              {
                icon: ShieldCheck,
                title: "Reliability",
                desc: "Our clients trust us for timely deliveries and consistent service.",
              },
              {
                icon: HeartHandshake,
                title: "Social Responsibility",
                desc: "We believe business success must translate into community progress.",
              },
            ].map((v, i) => (
              <div
                key={v.title}
                data-testid={`value-card-${i + 1}`}
                className={`flex flex-col gap-6 border border-zinc-200 bg-white p-7 ${
                  i % 2 === 1 ? "md:mt-10" : ""
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex h-11 w-11 items-center justify-center border border-zinc-950 text-zinc-950">
                    <v.icon className="h-5 w-5" strokeWidth={1.4} />
                  </div>
                  <span className="font-mono-plex text-[10px] uppercase tracking-[0.22em] text-zinc-400">
                    0{i + 1}
                  </span>
                </div>
                <div>
                  <div className="font-heading text-xl font-bold text-zinc-950">
                    {v.title}
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-600">
                    {v.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEADERSHIP MESSAGE */}
      <section
        data-testid="about-leadership"
        className="border-b border-zinc-200"
      >
        <div className="mx-auto max-w-[1400px] px-6 py-20 md:px-12 md:py-32 lg:px-16">
          <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
            <div className="md:col-span-3">
              <Eyebrow>03 · Leadership</Eyebrow>
              <div className="mt-6 font-mono-plex text-[11px] uppercase tracking-[0.22em] text-zinc-500">
                A Message from
              </div>
              <div className="mt-2 font-heading text-2xl font-bold text-zinc-950">
                Pankaj Dugar
              </div>
              <div className="font-mono-plex text-[11px] uppercase tracking-[0.22em] text-zinc-500">
                Founder
              </div>
            </div>

            <div className="md:col-span-9">
              <blockquote className="border-l-2 border-orange-500 pl-8">
                <p className="font-heading text-2xl font-semibold leading-snug tracking-tight text-zinc-950 md:text-4xl">
                  <span className="text-5xl font-black text-orange-500 align-top leading-none">
                    “
                  </span>
                  At Agrim Ventures, we believe that growth is meaningful only
                  when it's shared. Every bag of resin we deliver and every
                  youth we help skill is a step toward building a stronger,
                  more capable India.
                </p>
                <p className="mt-6 font-heading text-lg leading-relaxed text-zinc-600 md:text-xl">
                  We are grateful to our partners, clients, and team members
                  who make this journey possible.
                </p>
                <footer className="mt-8 font-mono-plex text-[11px] uppercase tracking-[0.22em] text-zinc-500">
                  — Pankaj Dugar, Founder · Agrim Ventures Private Limited
                </footer>
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* VERTICALS OVERVIEW */}
      <section
        data-testid="about-verticals"
        className="border-b border-zinc-200 bg-zinc-50"
      >
        <div className="mx-auto max-w-[1400px] px-6 py-20 md:px-12 md:py-32 lg:px-16">
          <Eyebrow>04 · Our Business Verticals</Eyebrow>
          <h2 className="mt-6 max-w-3xl font-heading text-3xl font-bold leading-tight tracking-tight text-zinc-950 md:text-5xl">
            Three verticals working in concert.
          </h2>

          <div className="mt-14 grid grid-cols-1 border-l border-t border-zinc-200 md:grid-cols-3">
            {[
              {
                no: "01",
                title: "Trading & Import of PET Resin",
                desc: "Serving beverage, bottling, and packaging industries with reliable supply and quality assurance.",
              },
              {
                no: "02",
                title: "Distribution Partnership",
                desc: "Authorized distributor for Chiripal Polyfilms Ltd through a wide, reliable distribution network.",
              },
              {
                no: "03",
                title: "CSR Skilling Initiatives",
                desc: "Empowering youth through in-house and outsourced training programs that deliver outcomes.",
              },
            ].map((c) => (
              <div
                key={c.no}
                className="flex min-h-[240px] flex-col justify-between border-b border-r border-zinc-200 bg-white p-8"
              >
                <span className="font-mono-plex text-[11px] uppercase tracking-[0.22em] text-zinc-400">
                  {c.no}
                </span>
                <div>
                  <div className="font-heading text-xl font-bold leading-tight text-zinc-950">
                    {c.title}
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-600">
                    {c.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12">
            <Link
              to="/our-business"
              data-testid="about-cta-business"
              className="group inline-flex items-center gap-3 bg-zinc-950 px-6 py-4 font-mono-plex text-[12px] font-semibold uppercase tracking-[0.22em] text-white transition-colors hover:bg-zinc-800"
            >
              Explore Our Business
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
